import React, { lazy, Suspense, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { cfg } from './runtime-config';
import './App.css';
import OktaApp from './OktaApp';
import { AppThemeProvider } from './theme';
import { createAppStore } from './store';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';

const initializeDevTools = async () => {
  if (process.env.NODE_ENV === 'development') {
    const { scan } = await import('react-scan');
    scan({ enabled: true });
  }
};

const initializeApm = async () => {
  if (cfg('APM_SERVER_URL')) {
    const { init } = await import('@elastic/apm-rum');
    init({
      serviceName: cfg('SERVICE_NAME'),
      serviceVersion: cfg('SERVICE_VERSION'),
      serverUrl: cfg('APM_SERVER_URL'),
      environment: cfg('NODE_ENV'),
    });
  }
};

setTimeout(() => {
  initializeDevTools();
  initializeApm();
}, 1000);

const store = createAppStore();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// The suspense fallback, shown before the theme's own components are available.
const loading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
    <CircularProgress />
  </Box>
);

const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((d) => ({ default: d.ReactQueryDevtools }))
);
const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools/build/lib/index.prod.js').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
);

const App = () => {
  const [showDevtools, setShowDevtools] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem('preLoginUrl') && window.location.pathname.includes('/login')) {
      sessionStorage.setItem('preLoginUrl', '/dashboard');
    }
    window.toggleDevtools = () => setShowDevtools((old) => !old);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={loading()}>
        <BrowserRouter>
          <OktaApp />
        </BrowserRouter>
        {process.env.NODE_ENV === 'development' && (
          <React.Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </React.Suspense>
        )}
        {showDevtools && <ReactQueryDevtoolsProduction />}
      </Suspense>
    </QueryClientProvider>
  );
};

const root = createRoot(document.getElementById('root'));

root.render(
  <Provider store={store}>
    <HelmetProvider>
      <AppThemeProvider>
        <App />
      </AppThemeProvider>
    </HelmetProvider>
  </Provider>
);
